<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { useI18n } from 'vue-i18n';
import KefubaoAPI from 'dashboard/api/kefubao';
import { useAlert } from 'dashboard/composables';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();
const { t } = useI18n();
const { getPlainText } = useMessageFormatter();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);
const chineseTranslation = ref('');
const isTranslatingToChinese = ref(false);
const translationError = ref('');

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const canTranslateToChinese = computed(() => {
  return !!content.value && !isEmpty.value;
});

const canCopyMessage = computed(() => {
  return messageType.value === MESSAGE_TYPES.INCOMING && !!content.value;
});

const plainTextContent = computed(() => {
  return getPlainText(content.value || '').replace(
    /[\u200B-\u200D\uFEFF]/g,
    ''
  );
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};

const translateToChinese = async () => {
  if (!canTranslateToChinese.value || isTranslatingToChinese.value) return;

  translationError.value = '';
  isTranslatingToChinese.value = true;

  try {
    const { data } = await KefubaoAPI.translate({
      text: content.value,
      targetLanguage: 'zh',
    });
    chineseTranslation.value = data.content || '';
  } catch (error) {
    translationError.value =
      error?.response?.data?.error || t('MESSAGE_TRANSLATION.ERROR');
  } finally {
    isTranslatingToChinese.value = false;
  }
};

const copyMessage = async () => {
  if (!canCopyMessage.value) return;

  await copyTextToClipboard(plainTextContent.value);
  useAlert(t('MESSAGE_TRANSLATION.COPIED'));
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <div
        v-if="canTranslateToChinese || canCopyMessage"
        class="-mt-1 flex flex-col gap-2"
      >
        <div class="flex flex-wrap gap-1.5">
          <button
            v-if="canCopyMessage"
            type="button"
            class="skip-context-menu inline-flex w-fit items-center gap-1 rounded-md border border-n-weak bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11 hover:border-n-brand hover:text-n-brand"
            @click.stop="copyMessage"
          >
            <span class="i-lucide-copy size-3.5" />
            <span>{{ $t('MESSAGE_TRANSLATION.COPY') }}</span>
          </button>
          <button
            v-if="canTranslateToChinese"
            type="button"
            class="skip-context-menu inline-flex w-fit items-center gap-1 rounded-md border border-n-weak bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11 hover:border-n-brand hover:text-n-brand disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isTranslatingToChinese"
            @click.stop="translateToChinese"
          >
            <span class="i-lucide-languages size-3.5" />
            <span>
              {{
                isTranslatingToChinese
                  ? $t('MESSAGE_TRANSLATION.LOADING')
                  : $t('MESSAGE_TRANSLATION.BUTTON')
              }}
            </span>
          </button>
        </div>
        <div
          v-if="chineseTranslation || translationError"
          class="rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
        >
          <div class="mb-1 text-xs font-semibold text-n-slate-11">
            {{ $t('MESSAGE_TRANSLATION.TITLE') }}
          </div>
          <div
            v-if="chineseTranslation"
            class="whitespace-pre-wrap break-words"
          >
            {{ chineseTranslation }}
          </div>
          <div v-else class="text-n-ruby-11">
            {{ translationError }}
          </div>
        </div>
      </div>
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
